import { useMemo } from 'react';
import { AlertTriangle, BarChart3, ListTodo } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DailySalesCard } from './DailySalesCard';
import { ChatInsightAlerts } from './ChatInsightAlerts';
import { ChatOnboarding } from './ChatOnboarding';
import { ChatRoutines, type Routine } from './ChatRoutines';

interface ChatWelcomeProps {
  onSelectPrompt: (prompt: string) => void;
  onSelectRoutine?: (routine: Routine) => void;
}

type TimeSlot = 'morning' | 'midday' | 'afternoon' | 'evening';

const GREETINGS: Record<TimeSlot, string> = {
  morning: 'Bonjour, prêt pour la journée ?',
  midday: 'Comment se passe la journée ?',
  afternoon: "Besoin d'un coup de main cet après-midi ?",
  evening: 'Bonne fin de journée !',
};

function getTimeSlot(): TimeSlot {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 10) return 'morning';
  if (hour >= 10 && hour < 14) return 'midday';
  if (hour >= 14 && hour < 18) return 'afternoon';
  return 'evening';
}

export function ChatWelcome({ onSelectPrompt, onSelectRoutine }: ChatWelcomeProps) {
  const { t } = useTranslation('dashboard');
  const greeting = useMemo(() => GREETINGS[getTimeSlot()], []);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-3.5 py-3 bg-[#f5f7f9] dark:bg-charcoal-900">
      {/* 1. Header Greeting section */}
      <div className="mx-auto flex w-full max-w-lg flex-col items-center text-center pt-1">
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
          {t('chat_welcome_greeting', { defaultValue: greeting })}
        </h1>
        <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400 leading-normal max-w-xs sm:max-w-sm">
          {t('chat_welcome_subtitle', {
            defaultValue: 'Posez une question sur votre commerce ou choisissez une piste.',
          })}
        </p>
      </div>

      {/* 2. Daily Sales KPI Widget Card */}
      <div className="mx-auto w-full max-w-lg">
        <DailySalesCard
          onClick={() => onSelectPrompt('Resume mes ventes du jour avec un conseil actionnable.')}
        />
      </div>

      {/* 3. Realtime Insight Alerts if any */}
      <div className="mx-auto w-full max-w-lg">
        <ChatInsightAlerts onSelectPrompt={onSelectPrompt} />
      </div>

      {/* 4. Suggestions du jour section */}
      <div className="mx-auto w-full max-w-lg" data-onboarding="suggestions">
        <div className="mb-2.5 px-1">
          <span className="text-[11px] font-mono font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
            SUGGESTIONS DU JOUR
          </span>
        </div>

        <div className="flex flex-col gap-3">
          {/* Suggestion 1: Full-width card */}
          <button
            type="button"
            onClick={() => onSelectPrompt('Resume mes ventes du jour avec un conseil actionnable.')}
            className="w-full bg-white dark:bg-charcoal-800 rounded-[24px] p-4 shadow-[0_2px_15px_rgba(0,0,0,0.03)] border-0 flex items-center gap-4 text-left transition-all hover:shadow-md active:scale-[0.99]"
          >
            <div className="size-11 rounded-2xl bg-[#e2f4d8] dark:bg-lime-950/60 text-[#2d6914] dark:text-lime-400 flex items-center justify-center shrink-0">
              <BarChart3 className="size-5" />
            </div>
            <span className="text-base font-semibold text-slate-900 dark:text-white flex-1 leading-snug">
              Résumé mes ventes du jour
            </span>
          </button>

          {/* Suggestion 2 & 3: 2-column Grid Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Left Card: Alertes stock */}
            <button
              type="button"
              onClick={() =>
                onSelectPrompt('Quels produits sont en rupture ou en alerte stock aujourd hui ?')
              }
              className="bg-white dark:bg-charcoal-800 rounded-[24px] p-4 shadow-[0_2px_15px_rgba(0,0,0,0.03)] border-0 flex flex-col justify-between items-start min-h-[115px] text-left transition-all hover:shadow-md active:scale-[0.99]"
            >
              <div className="size-10 rounded-2xl bg-[#fde8e8] dark:bg-rose-950/60 text-[#c81e1e] dark:text-rose-400 flex items-center justify-center shrink-0 font-bold text-sm">
                <AlertTriangle className="size-5 fill-current" />
              </div>
              <span className="text-base font-semibold text-slate-900 dark:text-white mt-4 leading-snug">
                Alertes stock
              </span>
            </button>

            {/* Right Card: 3 actions prioritaires */}
            <button
              type="button"
              onClick={() =>
                onSelectPrompt(
                  'Donne moi 3 actions prioritaires a faire aujourd hui pour améliorer ma boutique.',
                )
              }
              className="bg-white dark:bg-charcoal-800 rounded-[24px] p-4 shadow-[0_2px_15px_rgba(0,0,0,0.03)] border-0 flex flex-col justify-between items-start min-h-[115px] text-left transition-all hover:shadow-md active:scale-[0.99]"
            >
              <div className="size-10 rounded-2xl bg-[#f1f5f9] dark:bg-slate-800 text-[#475569] dark:text-slate-300 flex items-center justify-center shrink-0">
                <ListTodo className="size-5" />
              </div>
              <span className="text-base font-semibold text-slate-900 dark:text-white mt-4 leading-snug">
                3 actions prioritaires
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* 5. Optional routines section */}
      {onSelectRoutine && (
        <div className="mx-auto w-full max-w-lg mt-1">
          <ChatRoutines onSelectRoutine={onSelectRoutine} />
        </div>
      )}

      <ChatOnboarding />
    </div>
  );
}
